import { Grid, Step, StepLabel, Stepper } from '@mui/material';
import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import {
  createLpCapitalActionSuccessRoute,
  EQUALISATIONS_ADD,
} from 'adminConstants/routes';
import ErrorBoundary from 'components/ErrorBoundary';
import Page from 'components/new/Page';
import PageLoader from 'components/PageLoader';
import FormWithStateContextProvider from 'contexts/FormWithStateContext';
import { useLayoutContext } from 'contexts/LayoutContext';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Column } from 'further-ui/components/Layout';
import { useDisclosure } from 'further-ui/hooks';
import api from 'lib/trpcClient';
import CapitalActionDetails from './CapitalActionDetails';
import CapitalActionTransactions from './CapitalActionEntries';
import CapitalActionReview from './CapitalActionReview';
import useCapitalActionForm, { Steps } from './hooks/useCapitalActionForm';

const stepLabels = {
  [Steps.Details]: 'Create capital action',
  [Steps.Transactions]: 'Add transactions',
  [Steps.Review]: 'Approve action',
};

type Props = {
  vehicleId: string;
};

const AddEditCapitalAction: React.FC<Props> = ({ vehicleId }) => {
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();

  const [activeStep, setActiveStep] = useState<Steps>(Steps.Details);

  const [overpaymentPerCommitment] =
    api.capital.overpaymentPerCommitment.useSuspenseQuery({
      vehicleId,
    });

  const [listUncalledEqualisations] =
    api.capital.listUncalledEqualisations.useSuspenseQuery({
      vehicleId,
    });

  const { data: capitalAction, isLoading: isLoadingCapitalAction } =
    api.capitalAction.get.useQuery(
      {
        id: id!,
        vehicleId,
      },
      {
        enabled: !!id,
      },
    );

  const [commitments] = api.commitments.list.useSuspenseQuery({
    vehicleId,
    order: 'desc',
    page: 1,
    rowsPerPage: 1000,
    orderBy: 'commitmentDate',
  });
  const hasUncalledEqualisations = (listUncalledEqualisations?.length ?? 0) > 0;

  const { formMethods, handleSubmit, validateStep, isLoading } =
    useCapitalActionForm({
      id,
      vehicleId,
      defaultValues: capitalAction,
      hasUncalledEqualisations,
      onSubmitSuccess: (capitalActionId: string) => {
        history.push(createLpCapitalActionSuccessRoute(capitalActionId));
      },
    });

  const hasPendingCommitments = commitments.results?.some(
    (commitment) => !commitment.equalisationId,
  );

  const commitmentsPendingDialog = useDisclosure(hasPendingCommitments);

  const handleNext = async () => {
    if (await validateStep(activeStep)) {
      setActiveStep(activeStep + 1);
    }
  };

  const handlePrevious = () => {
    setActiveStep(activeStep - 1);
  };

  if (id && isLoadingCapitalAction) {
    return <PageLoader />;
  }

  const allocations = Object.fromEntries(
    capitalAction?.entries.map((entry, index) => [
      index,
      entry.capitalBreakdown,
    ]) ?? [],
  );

  const hasOverpayments = Object.values(overpaymentPerCommitment ?? {}).some(
    (overpayment) => !!overpayment,
  );

  const messages = hasUncalledEqualisations
    ? [
        {
          key: 'uncalled-equalisations',
          content:
            'This vehicle has an approved equalisation pending call. The equalisation must be the first item added to this capital action.',
          variant: 'info' as const,
          centered: true,
        },
      ]
    : hasOverpayments
    ? [
        {
          key: 'overpayments',
          content:
            'There are overpayments for some investors. Please review the overpayments and adjust the capital action accordingly.',
          variant: 'info' as const,
          centered: true,
        },
      ]
    : [];

  return (
    <Page messages={messages}>
      <Column spacing="lg" fullHeight>
        <Grid container>
          <Grid size={{ sm: 12, md: 12, lg: 8 }}>
            <Stepper activeStep={activeStep}>
              {Object.values(stepLabels).map((stepLabel) => {
                return (
                  <Step key={stepLabel}>
                    <StepLabel>{stepLabel}</StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Grid>
        </Grid>
        <FormWithStateContextProvider
          formMethods={formMethods}
          initialState={{ allocations, isEditForm: !!id }}
        >
          <ErrorBoundary loader={<PageLoader />}>
            {activeStep === Steps.Details && (
              <CapitalActionDetails onNext={handleNext} vehicleId={vehicleId} />
            )}
            {activeStep === Steps.Transactions && (
              <CapitalActionTransactions
                onPrevious={handlePrevious}
                onNext={handleNext}
                hasUncalledEqualisations={hasUncalledEqualisations}
              />
            )}
            {activeStep === Steps.Review && (
              <CapitalActionReview
                onPrevious={handlePrevious}
                onSubmit={formMethods.handleSubmit(handleSubmit)}
                isLoading={isLoading}
              />
            )}
          </ErrorBoundary>
        </FormWithStateContextProvider>
      </Column>
      <ConfirmDialog
        open={commitmentsPendingDialog.isOpen}
        onClose={commitmentsPendingDialog.onClose}
        title="Commitments pending close"
        content="There are commitments pending close. If you intend to include these commitments in this capital action, you must close and equalise those commitments before proceeding."
        actions={{
          confirm: {
            label: 'Close and equalise',
            onClick: () => {
              history.push(EQUALISATIONS_ADD);
            },
          },
          cancel: {
            label: 'Ignore and continue with capital action',
            onClick: commitmentsPendingDialog.onClose,
          },
        }}
        maxWidth="sm"
      />
    </Page>
  );
};

const AddEditCapitalActionWrapper: React.FC = () => {
  const { selectedVehicleId } = useLayoutContext();

  if (!selectedVehicleId) {
    return null;
  }

  return <AddEditCapitalAction vehicleId={selectedVehicleId} />;
};

export default AddEditCapitalActionWrapper;
